import { Navigate, Outlet } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAuthStatus } from './authSlice';
import { Spinner } from '@mmef/shared/components';

/**
 * The mirror of {@link RequireAuth}: for pages that only make sense when signed <em>out</em>.
 *
 * <p>Offering a sign-in form to someone who is already signed in is confusing at best — they fill
 * it in, get an email, and wonder what it's for.
 *
 * <p>The `unknown` branch waits rather than rendering the form. Guessing "probably signed out"
 * would show the form and then snatch it away a moment later, which is the flash this exists to
 * prevent. In practice the session check has usually resolved already, because it runs once when
 * the app boots; the wait is only visible on a cold load straight to this page.
 */
export function RequireAnonymous({ redirectTo = '/' }: { redirectTo?: string }) {
  const status = useSelector(selectAuthStatus);

  if (status === 'unknown') {
    return <Spinner label="Checking your session" />;
  }

  if (status === 'authenticated') {
    return <Navigate to={redirectTo} replace />;
  }

  return <Outlet />;
}
